import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Check, X, ExternalLink, Calendar } from "lucide-react";
import { api, assetUrl, affiliateGoUrl } from "@/lib/api";
import { trackAffiliateClick } from "@/lib/analytics";
import { Seo } from "@/components/Seo";
import { ScoreBadge } from "@/components/ScoreBadge";
import { Scorecard } from "@/components/Scorecard";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";

const REVIEW_TYPES = ["software_review", "product_review"];

export default function ArticleDetail() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const preview = params.get("preview");
  const [article, setArticle] = useState(null);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setArticle(null); setError(false);
    api.get("/scoring-config").then((r) => setCategories(r.data.categories));
    api.get(`/articles/${slug}${preview ? "?preview=1" : ""}`)
      .then((r) => setArticle(r.data))
      .catch(() => setError(true));
  }, [slug, preview]);

  if (error) return <NotFound />;
  if (!article) return <div className="max-w-3xl mx-auto px-4 py-24 font-mono-pm text-sm text-stone-500">Loading…</div>;

  const isReview = REVIEW_TYPES.includes(article.type);
  const date = (article.last_reviewed_at || article.updated_at || "").slice(0, 10);

  const schema = isReview && article.is_scored ? {
    "@context": "https://schema.org", "@type": "Review",
    itemReviewed: { "@type": "SoftwareApplication", name: article.product_name || article.title },
    reviewRating: { "@type": "Rating", ratingValue: article.overall_score, bestRating: 100, worstRating: 0 },
    author: { "@type": "Person", name: article.author || "Ramon Pasos" },
    publisher: { "@type": "Organization", name: "The PM Test" },
  } : { "@context": "https://schema.org", "@type": "Article", headline: article.title, author: { "@type": "Person", name: article.author || "Ramon Pasos" } };

  return (
    <article>
      <Seo title={article.meta_title || article.title} description={article.meta_description || article.excerpt}
        image={article.featured_image ? assetUrl(article.featured_image) : undefined}
        noindex={article.status !== "published"} type="article" schema={schema} />

      {article.status !== "published" && (
        <div className="bg-[#991B1B] text-white text-center py-2 font-mono-pm text-xs tracking-widest uppercase" data-testid="preview-banner">
          Preview — {article.status} (not public, noindex)
        </div>
      )}

      {/* header */}
      <header className="border-b-2 border-stone-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12">
          <Link to={isReview ? "/software" : "/guides"} className="font-mono-pm text-[10px] tracking-[0.25em] uppercase text-[#991B1B] hover:underline">← Back to reviews</Link>
          {article.category && <p className="font-mono-pm text-xs tracking-widest uppercase text-stone-400 mt-4">{article.category}</p>}
          <h1 className="font-serif-display text-4xl sm:text-5xl font-black leading-tight tracking-tight text-stone-900 mt-3">{article.title}</h1>
          {article.excerpt && <p className="text-xl text-stone-600 mt-4 leading-relaxed">{article.excerpt}</p>}
          <div className="flex items-center gap-4 mt-6 font-mono-pm text-xs text-stone-500">
            <span>By {article.author || "Ramon Pasos"}</span>
            {date && <span className="flex items-center gap-1.5"><Calendar size={12} /> Last reviewed {date}</span>}
          </div>
        </div>
      </header>

      {article.featured_image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-10">
          <img src={assetUrl(article.featured_image)} alt={article.featured_image_alt || article.title} className="w-full border-2 border-stone-900" />
        </div>
      )}

      {/* review verdict block */}
      {isReview && (
        <section className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
          <div className="border-2 border-stone-900 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 p-6 md:p-8 items-center border-b border-stone-900">
              <div className="flex items-center gap-5">
                {article.product_logo && <img src={assetUrl(article.product_logo)} alt={article.product_logo_alt || article.product_name} className="w-20 h-20 object-contain border border-stone-300 bg-white p-2" />}
                <div>
                  <p className="font-mono-pm text-xs tracking-widest uppercase text-stone-400">PM Test Verdict</p>
                  <h2 className="font-serif-display text-3xl font-black text-stone-900">{article.product_name || article.title}</h2>
                  {article.pricing && <p className="font-mono-pm text-sm text-stone-600 mt-1">{article.pricing}</p>}
                </div>
              </div>
              <div className="flex md:justify-end">
                <ScoreBadge score={article.overall_score} passes={article.passes} size="lg" unscored={!article.is_scored} />
              </div>
            </div>

            {!article.is_scored && (
              <div className="p-6 bg-stone-50 border-b border-stone-900">
                <p className="font-mono-pm text-xs text-stone-500 leading-relaxed">This review is in progress. We publish a PM Test Score only after completing all nine category evaluations.</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              {article.best_for && (
                <div className="p-6 border-b border-stone-900 md:border-r">
                  <p className="font-mono-pm text-[10px] tracking-widest uppercase text-stone-400 mb-2">Best For</p>
                  <p className="text-stone-800">{article.best_for}</p>
                </div>
              )}
              {article.pricing && (
                <div className="p-6 border-b border-stone-900">
                  <p className="font-mono-pm text-[10px] tracking-widest uppercase text-stone-400 mb-2">Pricing</p>
                  <p className="text-stone-800">{article.pricing}</p>
                </div>
              )}
            </div>

            {(article.strengths?.length > 0 || article.weaknesses?.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-stone-900">
                <div className="p-6 md:border-r border-stone-900">
                  <p className="font-mono-pm text-[10px] tracking-widest uppercase text-stone-900 mb-3 flex items-center gap-2"><Check size={14} className="text-green-700" /> Strengths</p>
                  <ul className="space-y-2">
                    {(article.strengths || []).map((s, i) => <li key={i} className="text-stone-800 flex gap-2"><Check size={16} className="text-green-700 shrink-0 mt-1" />{s}</li>)}
                  </ul>
                </div>
                <div className="p-6 border-t md:border-t-0 border-stone-900">
                  <p className="font-mono-pm text-[10px] tracking-widest uppercase text-stone-900 mb-3 flex items-center gap-2"><X size={14} className="text-[#991B1B]" /> Weaknesses</p>
                  <ul className="space-y-2">
                    {(article.weaknesses || []).map((s, i) => <li key={i} className="text-stone-800 flex gap-2"><X size={16} className="text-[#991B1B] shrink-0 mt-1" />{s}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {article.affiliate_link && (
              <div className="p-6 border-t border-stone-900 bg-stone-50">
                <a href={affiliateGoUrl(article.affiliate_link.id, article.slug)} target="_blank" rel="nofollow sponsored noopener noreferrer"
                  data-testid="affiliate-cta-button"
                  onClick={() => trackAffiliateClick({ url: article.affiliate_link.destination_url, partner: article.product_name || article.affiliate_link.name, placement: "review_cta" })}
                  className="inline-flex items-center gap-2 bg-stone-900 text-white px-8 py-4 font-mono-pm font-bold text-sm tracking-widest uppercase hover:-translate-y-1 hover:hard-shadow-red transition-transform duration-200">
                  Visit {article.product_name || "Website"} <ExternalLink size={16} />
                </a>
                <p className="font-mono-pm text-[10px] text-stone-400 mt-3">Affiliate link. We may earn a commission at no cost to you.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* scorecard */}
      {isReview && (
        <section className="max-w-5xl mx-auto px-4 sm:px-8 pb-10">
          <p className="font-mono-pm text-xs tracking-[0.25em] uppercase text-[#991B1B] mb-4">The Nine-Category Scorecard</p>
          <Scorecard scores={article.scores} categories={categories} />
        </section>
      )}

      {/* key features */}
      {isReview && article.key_features?.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-8 pb-10">
          <h2 className="font-serif-display text-2xl font-bold text-stone-900 mb-4">Key Features</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-l border-stone-900">
            {article.key_features.map((f, i) => (
              <div key={i} className="border-r border-b border-stone-900 p-4 bg-white text-stone-800 flex gap-2"><Check size={16} className="text-stone-900 shrink-0 mt-1" />{f}</div>
            ))}
          </div>
        </section>
      )}

      {/* body */}
      {article.body && (
        <section className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
          <div className="pm-prose" dangerouslySetInnerHTML={{ __html: article.body }} />
        </section>
      )}

      {/* screenshots */}
      {article.screenshots?.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
          <h2 className="font-serif-display text-2xl font-bold text-stone-900 mb-4">Screenshots</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {article.screenshots.map((s, i) => (
              <figure key={i}>
                <img src={assetUrl(s.url)} alt={s.alt || `${article.product_name} screenshot ${i + 1}`} className="w-full border-2 border-stone-900" />
                {s.alt && <figcaption className="font-mono-pm text-[11px] text-stone-500 mt-2">{s.alt}</figcaption>}
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* final verdict */}
      {isReview && article.final_verdict && (
        <section className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
          <div className="border-l-4 border-stone-900 bg-white p-6">
            <p className="font-mono-pm text-xs tracking-widest uppercase text-[#991B1B] mb-3">Final Verdict</p>
            <p className="text-lg text-stone-800 leading-relaxed">{article.final_verdict}</p>
          </div>
        </section>
      )}

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
        <AffiliateDisclosure />
      </section>
    </article>
  );
}

function NotFound() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      <Seo title="Not found" noindex />
      <p className="font-serif-display text-4xl font-black text-stone-900">Not found.</p>
      <p className="font-mono-pm text-sm text-stone-500 mt-4">This article isn't published yet, or the link is incorrect.</p>
      <Link to="/" className="inline-block mt-6 font-mono-pm text-sm tracking-widest uppercase underline">← Home</Link>
    </div>
  );
}
